import { Component, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { Params } from '../../../../services/utilis/add-params-service';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../../@shared/components/table-component/table-component';
import { WalletTransaction } from '../../../../models/wallet-transaction';
import { CustomerService } from '../../../../services/customers/customer-service';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { enumToOptions } from '../../../../@shared/utility/enumToOptions';
import { WalletTransactionType } from '../../../../models/enums';

@Component({
  selector: 'app-customer-wallet-transaction-history-list',
  imports: [TableComponent],
  templateUrl: './customer-wallet-transaction-history-list.html',
  styleUrl: './customer-wallet-transaction-history-list.scss'
})
export class CustomerWalletTransactionHistoryList implements OnInit {

  @ViewChild(TableComponent) table!: TableComponent<WalletTransaction>;
  @Input() customerID: string | undefined

  params = [
    "page",
    "size",
    'sortBy',
    'sortDirection',
    'transactionType',
    'createdFrom',
    'createdTo',
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    transactionType: "transactionType",
    createdFrom: "createdFrom",
    createdTo: "createdTo",
  }

  tableColumns: DynamicColumn<WalletTransaction>[] = [
    { header: 'ID', field: 'id', sortable: true, },
    {
      header: 'Transaction type',
      field: 'transactionType',
      sortable: true,
      isCapitalize: true,
      isFilterable: true,
      filterType: 'select',
      filterOptions: enumToOptions(WalletTransactionType),
      cellClass: (value) => {
        if (value === WalletTransactionType.ADMIN_CREDIT) return 'text-green';
        if (value === WalletTransactionType.ADMIN_DEBIT) return 'text-red';
        return '';
      }
    },
    {
      header: 'Amount',
      field: 'amount',
      suffixText: "AED",
      sortable: true,
    },
    {
      header: 'Balance after',
      field: 'balanceAfter',
      sortable: true,
      suffixText: "AED",
    },
    {
      header: 'Created by',
      field: 'createdByEmployeeName',
      sortable: true,
    },
    {
      header: 'Created at',
      field: 'createdAt',
      sortable: true,
      isDateTime: true,
    },
  ];

  walletTransactionsList = signal<WalletTransaction[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  createdDateFilter: CustomFilter = {
    key: "created",
    label: "Date",
    type: "date-range",
    options: []
  }

  private lastParams = new BehaviorSubject<Record<string, unknown> | null>(null);

  private customerService = inject(CustomerService);
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  ngOnInit() {
    this.getWalletTransactionsList();
  }

  getWalletTransactionsList = () => {
    this.activatedRoute.queryParams.pipe(
      debounceTime(0),
      filter(params => params['tab'] === '3'),

      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      filter((queryParams) => {
        // 🔥 only continue if params changed
        if (JSON.stringify(this.lastParams.value) === JSON.stringify(queryParams)) {
          return false;
        }
        this.lastParams.next(queryParams);
        return true;
      }),

      tap((queryParams) => {
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),

      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'createdAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.customerService.getCustomerWalletTransactionHistory(this.customerID!, params);
      })
    )
      .subscribe((res) => {
        this.walletTransactionsList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  resetAll = () => {
    this.table?.clearAllFilters();
  }

}
